import { ReactNode } from "react";
import { Button, Icon, Text } from "@ucl/react-library";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { InlineAlertMessage } from "src/components/inline-alert-message/inline-alert-message";
import { translationMapping } from "src/utils/translation-mappings";
import { CaseFormTypes } from "src/pages/case-landing/case-form-setup";

type StepConfig = {
  step: number;
  body: ReactNode;
};

type MultiStepFormLayoutProps = {
  methods: UseFormReturn<CaseFormTypes>;
  onSubmit: (data: CaseFormTypes) => Promise<void>;
  currentStep: number;
  totalSteps: number;
  steps: StepConfig[];
  showError: boolean;
  onCloseError: () => void;
  errorTitleKey: string;
  errorBodyKey: string;
  submitLabelKey: string;
  continueLabelKey: string;
  submitDisabled?: boolean;
  handleContinue: () => void;
  handleBack: () => void;
  hideActions?: boolean;
};

export const MultiStepFormLayout = ({
  methods,
  onSubmit,
  currentStep,
  totalSteps,
  steps,
  showError,
  onCloseError,
  errorTitleKey,
  errorBodyKey,
  submitLabelKey,
  continueLabelKey,
  submitDisabled,
  handleContinue,
  handleBack,
  hideActions = false,
}: MultiStepFormLayoutProps) => {
  const { t } = useTranslation();

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col items-start gap-10">
        <div className="flex flex-col items-start gap-3">
          <Text.P size="large" className="!text-primary">
            {t("challenge.stepCounter", { current: currentStep, total: totalSteps })}
          </Text.P>

          <div className="w-full">
            {steps.map(({ step, body }) => (
              <div key={step} hidden={step !== currentStep} className="w-full">
                {body}
              </div>
            ))}
          </div>
        </div>

        {showError && (
          <InlineAlertMessage
            title={t(errorTitleKey)}
            body={<Trans i18nKey={errorBodyKey} components={translationMapping(t)} />}
            onClose={onCloseError}
          />
        )}

        {!hideActions && (
          <div className="flex w-full flex-col gap-3 sm:flex-row-reverse sm:!justify-between">
            {currentStep === totalSteps ? (
              <Button type="submit" disabled={submitDisabled}>
                {t(submitLabelKey)}
              </Button>
            ) : (
              <Button type="button" onClick={handleContinue}>
                {t(continueLabelKey)}
              </Button>
            )}

            <Button variant="tertiary" onClick={handleBack}>
              <Icon icon="caretLeft" />
              {t("common.goBack")}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
